/**
 * Spacing-Tokens für alle mQorva-Frontends.
 *
 * Spiegelt die CSS-Custom-Properties aus `shared/frontend/ui/tokens.css`
 * (`--mq-page-gap`, `--mq-section-gap`, `--mq-card-padding`, …) als TypeScript-Werte.
 *
 * Verwendung: überall dort, wo bisher ein Abstand direkt als Zahlenwert hartkodiert
 * wurde. Wo CSS greift (Card-Padding, Page-Gap), übernehmen die Layout-Komponenten
 * `<PageContainer>` / `<SectionCard>` das automatisch — dann sind diese Tokens nur
 * für Sonderfälle nötig.
 *
 * Quelle der Wahrheit für visuelle Abstände bleibt `ui/tokens.css`; ändert sich
 * dort ein Wert, ist er hier nachzuziehen.
 */
export const spacing = {
  /** Abstand zwischen Top-Level-Sections einer Page (Flow zwischen Cards). */
  pageGap: 20,
  /** Abstand zwischen Blöcken innerhalb einer Section. */
  sectionGap: 16,
  /** Standard-Innenabstand einer Card. */
  cardPadding: 20,
  /** Kompakter Innenabstand (size="small" / dichte Listen). */
  cardPaddingCompact: 16,
  /** Abstand zwischen eng zusammengehörenden Elementen (z. B. Label + Wert). */
  elementGap: 12,
  /** Feiner Abstand (Icon + Text, Tag-Reihen). */
  tightGap: 8,
  /** Sehr feiner Abstand (mehrzeilige Labels). */
  microGap: 4,
} as const;

export type SpacingToken = keyof typeof spacing;

/** Application chrome measurements, preserving the established full-width title bar. */
export const appShellMetrics = {
  hairline: 1,
  headerHeight: 56,
  headerZIndex: 100,
  desktopHeaderZIndex: 11000,
  criticalBannerZIndex: 10001,
  logoHeight: 28,
  mobileLogoHeight: 24,
  titleFontSize: 18,
  titleLineHeight: 32,
  accountAvatarSize: 28,
  userMenuWidth: 300,
  identityPadding: 2,
  collapsedWidth: 80,
  collapseBarHeight: 40,
  creditBarHeight: 36,
  creditHighlightMs: 1500,
  navigationIndent: 24,
  navigationMinWidth: 220,
  navigationDefaultWidth: 232,
  navigationMaxWidth: 420,
  navigationItemGutter: 32,
  navigationIconWidth: 28,
  navigationArrowWidth: 20,
  navigationTextSafetyPadding: 12,
  navigationAverageCharacterWidth: 8,
  mobileNavigationWidth: "min(88vw, 360px)",
  contentPadding: 16,
  headerActionGap: 10,
  contextStatusGap: 6,
  contextButtonPadding: 30,
  contextArrowWidth: 12,
  contextFallbackCharacterWidth: 7.5,
  contextMaxWidth: 300,
  contextNarrowMaxWidth: 160,
  contextNarrowHeight: 30,
  contextLabelLineHeight: 20,
  contextNarrowPaddingInline: 10,
  contextStatusDotWidth: 8,
  /** Zahl am Kontexteintrag (bis „99+“), gleiche Zahlenmarke wie in der Navigation. */
  contextStatusCountWidth: 30,
  contextCheckMarkerWidth: 14,
  pinGap: 6,
  pinMargin: 24,
  pinChipHeight: 38,
  pinChipMaxWidth: 172,
  pinAvatarSize: 28,
  pinIconFontSize: 16,
  pinSeparatorHeight: 22,
  pinCloseButtonSize: 16,
  pinCloseButtonOffset: -2,
} as const;

export const typography = {
  /** Sehr kleiner Zeitstempel oder technische Mikro-Metadaten. */
  xxs: 10,
  /** Sehr kleine Zusatzinformation, z. B. Zeitstempel in kompakten Statuslisten. */
  xs: 11,
  /** Kleine Nebeninformation, kompakte Badges oder Tabellenmetadaten. */
  sm: 12,
  /** Sekundärtext in kompakten Arbeitsflächen. */
  md: 13,
  /** Standard-Fließtextgröße, falls Ant-Design-Komponenten nicht selbst skalieren. */
  base: 14,
  /** Kompakte Zwischenüberschrift innerhalb dichter Panels. */
  titleSm: 16,
  /** Hervorgehobene Kennzahl oder Zwischenüberschrift. */
  titleMd: 20,
  /** PageHeader-Titelgröße. */
  pageTitle: 24,
  lineHeightTight: 1.25,
  lineHeightBase: 1.35,
  lineHeightReadable: 1.55,
} as const;

export const radii = {
  control: 6,
  card: 8,
  pill: 999,
} as const;

export type TypographyToken = keyof typeof typography;
